import { PaymentConfigurationError } from "./utils/errors/PaymentConfigurationError";
import { createPaymentLogger } from "./utils/payment-logger";
import PaymentGateway from "./gateways/PaymentGateway";
// eslint-disable-next-line import/no-cycle
import StripePaymentGateway from "./gateways/StripePaymentGateway/StripePaymentGateway";
import AuthorizePaymentGateway from "./gateways/AuthorizePaymentGateway/AuthorizePaymentGateway";
import ShowpassPaymentGateway from "./gateways/ShowpassPaymentGateway/ShowpassPaymentGateway";
import YunoPaymentGateway from "./gateways/YunoPaymentGateway/YunoPaymentGateway";
import useBasketPaymentService, {
  PaymentServiceBasket,
} from "./services/useBasketPaymentService";
import { isAuthorizeBasketGateway } from "./utils/authorize/authorize";
import { isStripeBasketGateway } from "./utils/stripe/stripe";
import { isShowpassBasketGateway } from "./utils/showpass/showpass";
import { isYunoBasketGateway } from "./utils/yuno/yuno";
import useInvoicePaymentService from "./services/useInvoicePaymentService";
import { type PaymentManagerConfig } from "./PaymentManager";

const log = createPaymentLogger("PaymentGatewayFactory");

/**
 * A factory responsible for selecting a PaymentGateway based on
 * the basket's (or more broadly, the environment's) characteristics.
 *
 * We use the singleton pattern for this as our handlers should be able to create/use an existing PaymentGateway that has previously been instantiated.
 * For example, given a venue using Stripe as a payment gateway should use the StripePaymentGateway class
 * for both RegularPaymentHandler and AffirmPaymentHandler
 *
 * Payment Manager -> PaymentHandlerFactory -> PaymentHandler -> PaymentGatewayFactory -> PaymentGateway
 */
class PaymentGatewayFactory {
  // Map that stores gateway instances by a composite key of gateway name, publishable API key and for added safety -- currency.
  private static gatewayInstances = new Map<string, PaymentGateway>();

  static getOrCreateGateway(
    config: PaymentManagerConfig,
    basket: PaymentServiceBasket,
    services: {
      basketPaymentService: ReturnType<typeof useBasketPaymentService>;
      invoicePaymentService: ReturnType<typeof useInvoicePaymentService>;
    }
  ): PaymentGateway {
    const GatewayClass = PaymentGatewayFactory.getGatewayClass(basket);

    // Create a unique key based on gateway name and publishable API key
    // At time of writing, publishable_api_key us the field for API keys even on non-Stripe processors.
    const publishableKey =
      basket.paymentGateway?.publishable_api_key || Date.now().toString(36);
    const currency =
      basket.paymentGateway?.currency ||
      basket.currency ||
      Date.now().toString(36);
    const gatewayKey = `${GatewayClass.gatewayName}:${publishableKey}:${currency}`;

    let gateway = PaymentGatewayFactory.gatewayInstances.get(gatewayKey);

    if (!gateway) {
      log.info(`Creating new gateway instance for ${gatewayKey}`);
      gateway = new GatewayClass(config, services);
      PaymentGatewayFactory.gatewayInstances.set(gatewayKey, gateway);
    } else {
      log.info(`Using existing gateway instance for ${gatewayKey}`);
    }

    return gateway;
  }

  private static getGatewayClass(basket: PaymentServiceBasket) {
    if (isShowpassBasketGateway(basket)) {
      return ShowpassPaymentGateway;
    }

    if (isStripeBasketGateway(basket)) {
      return StripePaymentGateway;
    }

    if (isAuthorizeBasketGateway(basket)) {
      return AuthorizePaymentGateway;
    }

    if (isYunoBasketGateway(basket)) {
      return YunoPaymentGateway;
    }

    // Throw on an unknown gateway. No defaults here due to basket.payment_gateway being a discrete set.
    throw new PaymentConfigurationError(
      `Unsupported basket gateway: ${basket.paymentGateway?.gateway_type}`,
      {
        metadata: {
          gatewayType: basket.paymentGateway?.gateway_type,
          basketId: basket.basketId,
        },
      }
    );
  }
}

export default PaymentGatewayFactory;
